import { useCallback, useEffect, useState } from "react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { PartialAgent } from "~/services/agents";
import {
  deleteTurnFeedback,
  putTurnFeedback,
  type FeedbackCreate,
  type TurnFeedback as TurnFeedbackValue,
} from "~/services/turns";
import { Markdown } from "./Markdown";
import { ToolCallsDisclosure } from "./ToolCallsDisclosure";
import { TurnFeedback } from "./TurnFeedback";
import type { AssistantTurnData } from "./useTurns";

interface AssistantTurnProps {
  turn: AssistantTurnData;
  agent: PartialAgent | null;
  conversationId: string;
  // True between the user clicking Stop and the run ending. Only meaningful
  // on the in-flight turn (the one with `turn.isRunning`); changes the
  // status label from "Thinking" to "Stopping" so the chat area itself
  // reflects the pending cancel.
  isCancelling?: boolean;
}

// Renders one assistant turn as a single avatar plus a vertical stack:
//   - "Thinking…" / "Stopping…" status while the turn is in-flight
//   - collapsible "N tool calls" disclosure (when there are any)
//   - the final assistant text (the last text-message in the turn)
//   - "Response cancelled" marker when the turn ended via cancellation
//   - thumbs up/down feedback bar once the turn has finished
//
// Multiple interim text messages within a turn are collapsed by the
// `useTurns` reducer — only the latest survives, matching the agent's
// intent (each prior text-message was a working thought it later revised).
export const AssistantTurn = ({
  turn,
  agent,
  conversationId,
  isCancelling,
}: AssistantTurnProps) => {
  const showThinking = turn.isRunning;
  const hasTools = turn.toolCalls.length > 0;
  const hasText = turn.text.length > 0;
  const showCancelled = turn.cancelled && !turn.isRunning;
  const showFeedback = !turn.isRunning && !turn.cancelled && !!turn.turnId;

  const [feedback, setFeedback] = useState<TurnFeedbackValue | FeedbackCreate | null>(
    turn.feedback ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // True iff we watched this turn stream to completion in the current
  // session. Turns loaded from /connect history mount with isRunning=false
  // and stay that way — `sawRunning` never flips and the feedback bar
  // appears without animation. Live turns mount with isRunning=true, the
  // effect flips `sawRunning` on the next tick, and when isRunning later
  // becomes false the feedback bar renders with the entrance animation.
  const [sawRunning, setSawRunning] = useState(false);
  useEffect(() => {
    if (turn.isRunning) setSawRunning(true);
  }, [turn.isRunning]);

  // Re-sync if /connect later returns a different feedback value for this
  // turn (e.g. the conversation reloaded after a navigation).
  useEffect(() => {
    setFeedback(turn.feedback ?? null);
  }, [turn.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text: string | null, impact: number | null) => {
      if (!turn.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const payload: FeedbackCreate = {
          feedback_type: "BINARY",
          feedback_value: value,
          text,
          impact,
        };
        const result = await putTurnFeedback(conversationId, turn.turnId, payload);
        setFeedback(result);
      } catch (err) {
        console.error("Failed to submit turn feedback", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, isSubmitting, turn.turnId]
  );

  const handleClear = useCallback(async () => {
    if (!turn.turnId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteTurnFeedback(conversationId, turn.turnId);
      setFeedback(null);
    } catch (err) {
      console.error("Failed to clear turn feedback", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [conversationId, isSubmitting, turn.turnId]);

  return (
    <li className="flex w-full items-start gap-3 self-start">
      <AgentAvatar id={agent?.id} name={agent?.name} size={28} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {showThinking && (
          <div className="inline-flex items-center text-xs" aria-live="polite">
            <ShimmerText>{isCancelling ? "Stopping" : "Thinking"}</ShimmerText>
          </div>
        )}
        {hasTools && <ToolCallsDisclosure entries={turn.toolCalls} />}
        {hasText && (
          <div className="px-1 py-1 text-sm text-foreground">
            <Markdown text={turn.text} />
          </div>
        )}
        {showCancelled && (
          <div className="px-1 text-xs italic text-muted-foreground" aria-live="polite">
            Response cancelled
          </div>
        )}
        {showFeedback && (
          <div
            className={
              sawRunning
                ? "-ml-1 mt-0.5 animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300 delay-150 ease-out"
                : "-ml-1 mt-0.5"
            }
          >
            <TurnFeedback
              feedback={feedback}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onClear={handleClear}
            />
          </div>
        )}
      </div>
    </li>
  );
};
